import { encounterCreatureField, encounterField } from '../shared/array-types';
import { withAsyncDeadline } from '../shared/async-deadline';
import { fetchSavedResponse, readSavedResponse } from '../shared/read-saved-response';
import { statusRows } from '../janus-engine/statuses/expiry';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { planPreparedSpellStatuses } from '../janus-engine/compile/prepared-spell-delta';
import { savedCreatureRelocationFrame, type RelocationMemberBatch } from '../janus-engine/compile/saved-creature-relocation';
import type { BaseEnvelope, BasePatchOp } from '../janus-engine/compile/base-patch';
import type { AppliedFrame, AppliedTouchedRow } from '../janus-engine/transport/applied-frame';
import type { ComputedEntityDelta } from '../janus-engine/compile/action-computed-delta';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').toUpperCase();
const memberId = (row: Row): string => upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id'));
type Recovery = { roster_ids: string[]; encounters: Row; rows: AppliedTouchedRow[]; deletes: AppliedFrame['deletes'] };
type Host = Window & { ftCompileRelocationMembers?: (envelopes: readonly BaseEnvelope[]) => Promise<RelocationMemberBatch> };

/** A clock conflict reads no held entity again. Only previously unknown arrivals need a base read. */
export function timeConflictPlan(encounter: Row, authoritative: Row | null) {
    const saved = record(authoritative?.time_recovery) as Recovery | null;
    if (!saved || !Array.isArray(saved.roster_ids) || !record(saved.encounters)
        || !Array.isArray(saved.rows) || !Array.isArray(saved.deletes)) return null;
    const roster = Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr as Row[] : [];
    const held = new Set(roster.map(memberId)), current = new Set(saved.roster_ids.map(upper));
    return { saved, added: [...current].filter(id => !held.has(id)), removed: [...held].filter(id => !current.has(id)) };
}

/** Native computed-path deltas keep unrelated member clusters borrowed. */
function statusDelta(row: Row, frame: AppliedFrame, encounter: Row): ComputedEntityDelta {
    const statuses = [...statusRows(row, 'target'), ...statusRows(row, 'caster')];
    const removed = new Set(frame.deletes.filter(entry => entry.table === 'statuses').map(entry => upper(entry.row_id)));
    const ops: Row[] = [...new Set(statuses.map(status => upper(readItemField(status, 'status_id'))))]
        .filter(id => removed.has(id)).map(id => ({ op: 'status_remove', status_id: id }));
    for (const entry of frame.rows.filter(entry => entry.table === 'statuses')) {
        const target = upper(readItemField(entry.row, 'status_target_id'));
        if (target === memberId(row)) ops.push({ op: 'status_upsert', entry: entry.row });
        else if (statuses.some(status => upper(readItemField(status, 'status_id')) === upper(entry.row_id)))
            ops.push({ op: 'status_remove', status_id: entry.row_id });
    }
    const catalog = record(encounter.catalog) ?? {};
    const projection = planPreparedSpellStatuses(row, ops, catalog, () => encounter.encounter_creatures_arr as Row[],
        id => record(record(row.catalog)?.[id] ?? catalog[id]));
    const delta: ComputedEntityDelta = { set: [], unset: [], mirrors: { set: [], unset: [] } };
    for (const key of projection.paths) {
        const path = key.split('.'), computed = path[0] === 'computed';
        if (computed) path.shift();
        const parent = computed ? record(projection.row.computed) : projection.row;
        let value: unknown = parent;
        for (const part of path) value = record(value)?.[part];
        const patch = computed ? delta : delta.mirrors!;
        if (value === undefined) patch.unset.push(path); else patch.set.push({ path, value });
    }
    return delta;
}

export async function refreshTimeConflict(encounterId: string, authoritative: Row | null, operationId: string): Promise<string | null> {
    const encounter = window.FateTide?.encounterCache?.get(encounterId) as unknown as Row | undefined;
    const plan = encounter && timeConflictPlan(encounter, authoritative);
    if (!encounter || !plan) return 'recovery-snapshot-unavailable';
    let stage = 'members';
    try {
        return await withAsyncDeadline(async signal => {
            const host = window as Host;
            const envelopes = await Promise.all(plan.added.map(async id => {
                const response = await fetchSavedResponse(`${BASE_URL}options/api/base_entity_inc.php?entity_id=${encodeURIComponent(id)}&entity=creature&cold=0`, { signal });
                if (!response.ok) throw new Error('recovery-member-read');
                const envelope = await readSavedResponse(response) as BaseEnvelope;
                if (!envelope || 'resync' in envelope || upper(envelope.entity_id) !== id
                    || upper(encounterCreatureField(envelope.base.encounter_creatures, 'encounter_id')) !== upper(encounterId)) throw new Error('recovery-members-changed');
                return envelope;
            }));
            signal.throwIfAborted();
            stage = 'compile';
            if (envelopes.length && !host.ftCompileRelocationMembers) return 'recovery-compiler-unavailable';
            const arrivals = envelopes.length ? await host.ftCompileRelocationMembers!(envelopes) : { members: [], failures: [] };
            signal.throwIfAborted();
            if (arrivals.failures.length || arrivals.members.length !== plan.added.length) return 'recovery-member-compile-refused';
            const frame: AppliedFrame = { kind: 'applied', operation_id: `time-recovery:${operationId}`, encounter_id: encounterId,
                actor_id: '', rev: 0, member_revs: {}, rows: plan.saved.rows,
                computed: {}, computed_delta: {}, new_entities: [], deletes: plan.saved.deletes };
            if (plan.added.length || plan.removed.length) {
                const envelope = encounter._entity_base as BaseEnvelope | undefined;
                if (!envelope) return 'recovery-membership-base-unavailable';
                const ops: BasePatchOp[] = [
                    ...plan.removed.map(id => ({ entity_id: encounterId, base_rev: envelope.base_rev, table: 'encounter_creatures', row_id: id, op: 'delete' as const, cols: {} })),
                    ...arrivals.members.map(member => ({ entity_id: encounterId, base_rev: envelope.base_rev, table: 'encounter_creatures', row_id: member.entity_id,
                        op: 'upsert' as const, cols: record(member.base.encounter_creatures)! })),
                ];
                const membership = savedCreatureRelocationFrame(envelope, ops, arrivals.members, encounter);
                if (!membership) return 'recovery-membership-unavailable';
                frame.new_entities = membership.new_entities;
                frame.inputs = membership.inputs;
                frame.deletes = [...frame.deletes, ...membership.deletes];
            }
            frame.rows = [...frame.rows, { table: 'encounters', row_id: encounterId, row: plan.saved.encounters, partial: true }];
            const roster = encounter.encounter_creatures_arr as Row[];
            const touched = new Set(frame.rows.filter(entry => entry.table === 'encounter_creatures').map(entry => upper(entry.row_id)));
            const statusIds = new Set([...frame.rows.filter(entry => entry.table === 'statuses'), ...frame.deletes.filter(entry => entry.table === 'statuses')].map(entry => upper(entry.row_id)));
            for (const entry of frame.rows.filter(entry => entry.table === 'statuses'))
                for (const key of ['status_target_id', 'status_caster_id']) touched.add(upper(readItemField(entry.row, key)));
            for (const row of roster) if (touched.has(memberId(row))
                || [...statusRows(row, 'target'), ...statusRows(row, 'caster')].some(status => statusIds.has(upper(readItemField(status, 'status_id')))))
                frame.computed_delta![memberId(row)] = statusDelta(row, frame, encounter);
            signal.throwIfAborted();
            if (!window.ftApplyBasePatch) return 'recovery-adopter-unavailable';
            stage = 'adopt';
            await window.ftApplyBasePatch({ type: 'base_patch', encounter_id: encounterId, ops: [], applied: frame });
            signal.throwIfAborted();
            const held = window.FateTide?.encounterCache?.get(encounterId) as unknown as Row | undefined;
            const current = held && timeConflictPlan(held, authoritative);
            if (!current || current.added.length || current.removed.length
                || Number(encounterField(held, 'in_combat') ?? 0) !== Number(encounterField(plan.saved.encounters, 'in_combat') ?? 0)) return 'recovery-adoption-incomplete';
            return null;
        }, 180000);
    } catch (error) {
        window.dispatchEvent(new CustomEvent('ft:time-recovery-failed', { detail: { encounterId, stage,
            error: error instanceof Error ? error.message : String(error) } }));
        return error instanceof DOMException && error.name === 'TimeoutError' ? `recovery-${stage}-timeout`
            : error instanceof Error && error.message.startsWith('recovery-') ? error.message : `recovery-${stage}-failed`;
    }
}
